"use client";

import { useRef } from "react";
import { TimerOff } from "lucide-react";
import { DARK_BAND, GradText, SectionHead, SectionShell, useBandRowAlign, useWholeRowBlocks } from "@/components/sections/kit";
import { useEnterStagger } from "@/components/sections/kit/motion";
import { routePath } from "@/lib/routes";
import LinkRow, { LinkSheet, RowChip } from "./LinkRow";

const EXPIRY_SUMMARY =
  "Example of link expiry, set through the API or MCP: go.example.com/earlybird points at " +
  "example.com/tickets with a 302 status code and expires on 30 September 2026. After that moment " +
  "the link answers 410 Gone instead of redirecting, so nothing fails silently.";

/**
 * Lifetime — link expiry on the page's one dark band: the link rows wearing
 * an expiry pill, plus the honest end state (410 Gone, never a silent 404 —
 * the router really answers 410 after `expires_at`). Expiry is set through
 * the public API or MCP (create_link / update_link), on every plan; the
 * dashboard doesn't show it. There is no password gate (claims audit
 * 2026-09-27), so none is drawn.
 */
export default function AccessLifetimeSection() {
  const rootRef = useRef<HTMLDivElement>(null);
  useWholeRowBlocks(rootRef);
  useBandRowAlign(rootRef);
  useEnterStagger(rootRef, "[data-rise]", { y: 22, stagger: 0.1 });

  return (
    <SectionShell id="access" band="dark" className="scroll-mt-16 max-md:bg-[#14141c]">
      <SectionHead
        tone="light"
        gridFill={DARK_BAND}
        eyebrow="Lifetime"
        title={
          <>
            Decide how long <GradText>a link lives.</GradText>
          </>
        }
        lead={
          <span className="text-white/70">
            Account links never expire unless you say so — and an expiry ends a link cleanly, with a 410,
            never a silent 404.
          </span>
        }
      />

      <div ref={rootRef} className="flex flex-col">
        {/* The expiry sheet and its caption share one grid row on lg+ (sheet
            left, caption right); on phones the caption follows the sheet. */}
        <div data-grid-block data-grid-fill={DARK_BAND} className="grid items-center gap-x-[var(--grid-cell,72px)] gap-y-5 lg:grid-cols-2">
          {/* Expiry */}
          <div data-rise className="min-w-0">
            <p className="sr-only">{EXPIRY_SUMMARY}</p>
            <div aria-hidden="true" className="h-full">
              <LinkSheet tone="light" label="LINKS · SET VIA API OR MCP" note="LIFETIME" fill>
                <LinkRow
                  tone="light"
                  slug="go.example.com/menu"
                  destination="example.com/menu"
                  meta="no expiry · default"
                />
                <LinkRow
                  tone="light"
                  slug="go.example.com/earlybird"
                  destination="example.com/tickets"
                  meta="27 days left"
                  extras={
                    <RowChip tone="light" accent>
                      <TimerOff size={10} className="mr-1" aria-hidden /> expires 30 Sep 2026
                    </RowChip>
                  }
                />
                <LinkRow
                  tone="light"
                  slug="go.example.com/q3-offer"
                  destination="example.com/offers/q3"
                  meta="expired 3 days ago"
                  code={410}
                  muted
                />
                <div className="mt-auto px-[18px] py-[14px]">
                  <div className="font-jetbrains text-[11px] tracking-[0.06em] text-white/45">AFTER EXPIRY</div>
                  <div className="mt-2 flex items-center gap-2.5 font-jetbrains text-[13px]">
                    <span className="rounded-md border border-white/15 bg-white/[0.06] px-[7px] py-[2px] text-[11px] font-semibold text-white/80">
                      410
                    </span>
                    <span className="text-white/70">Gone — “Link expired”</span>
                  </div>
                </div>
              </LinkSheet>
            </div>
          </div>

          <div data-rise className="min-w-0">
            <h3 className="text-[17px] font-bold tracking-[-0.01em] text-white">Link expiry</h3>
            <p className="mt-2 max-w-[46ch] text-[14.5px] leading-[1.6] text-white/65">
              Set an expiry when you create or update a link through the API or your AI agent.
              After it, the link answers 410 Gone — an honest end state that browsers, crawlers and
              humans all understand — instead of quietly 404ing or, worse, still redirecting to a
              sale that ended.
            </p>
          </div>

          <p data-rise className="mt-3 font-jetbrains text-[11px] tracking-[0.04em] text-white/40 lg:col-span-2">
            Account links never expire unless you say so, on any plan. Expiry dates are set through the API
            or MCP on every plan; the dashboard doesn’t show them —{" "}
            <a href={routePath("pricing")} className="text-white/60 underline underline-offset-2 hover:text-white">
              see the plan matrix
            </a>
            .
          </p>
        </div>
      </div>
    </SectionShell>
  );
}
